import React from "react";
import { isBrowser } from "react-device-detect";
import { NavLink } from "react-router-dom";
import Button from "ui/components/atoms/Button";
import Icon from "ui/components/atoms/Icon";
import ContactModal from "ui/components/organisms/ContactModal";
import { requestContact } from "core/services";
import { cx } from "utils/join-classnames";
import faqItems from "core/constants/faq";
import { useNotifications } from "ui/context/NotificationsContext";
import { useSessionContext } from "ui/context/SessionContext";
import { Logo } from "ui/components";
import $ from "./Support.module.scss";
import pwa_styles from "./Support_Pwa.module.scss";

const Support: React.FC = () => {
    const { addNotification } = useNotifications();
    const sessionContext = useSessionContext();
    const [showContactModal, setShowContactModal] =
        React.useState<boolean>(false);
    const [activeQuestion, setActiveQuestion] = React.useState<number>();

    const handleContactModalClose = (): void => {
        setShowContactModal(false);
    };
    const handleContactModalOpen = (): void => {
        setShowContactModal(true);
    };

    const handleContactModalSend = async (message: string): Promise<void> => {
        try {
            if (!sessionContext.session?.token) {
                throw Error("missing token");
            }
            if (!sessionContext.selectedProject?.id) {
                throw Error("missing token");
            }
            const result = await requestContact(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                message,
            );
            if (result.error) {
                throw Error("Contract request failed");
            }
            setShowContactModal(false);
            addNotification(
                "success",
                "Message sent",
                "We will be in touch",
                5000,
            );
        } catch (err) {
            addNotification(
                "error",
                "Contact request failed",
                "Please try again",
                5000,
            );
        }
    };

    if (!isBrowser) {
        return (
            <div className={pwa_styles.page}>
                <div className={pwa_styles.header}>
                    <div className={pwa_styles.header__icon__container}>
                        <NavLink to="/clips">
                            <Icon
                                className={pwa_styles.header__icon}
                                name="arrow-left"
                            />
                        </NavLink>
                    </div>
                    <div className={pwa_styles.header__logo}>
                        <Logo />
                    </div>
                </div>
                <div className={pwa_styles.subheader}>
                    <h3 className={pwa_styles.subheader__title}>Support</h3>
                    <div className={pwa_styles.subheader__hr} />
                </div>

                <div>
                    <div className={pwa_styles.content__text}>
                        Are you struggling with anything, or have any questions?
                        Contact us below!
                    </div>
                    <div className={pwa_styles.content__button}>
                        <Button
                            type="primary"
                            theme="light"
                            icon="email"
                            onClick={handleContactModalOpen}
                        >
                            E-mail us
                        </Button>
                    </div>
                </div>
                {showContactModal && (
                    <ContactModal
                        onSend={handleContactModalSend}
                        onClose={handleContactModalClose}
                    />
                )}
            </div>
        );
    }

    return (
        <div className={$.page}>
            <div className={$.heading}>
                <div className={$.heading__left}>
                    <h1 className={cx($.heading, $.heading__primary)}>
                        Support
                    </h1>
                    <span className={$.heading__desc}>
                        See SpotOn&apos;s Frequently Asked Questions.
                    </span>
                </div>
                <div className={$.heading__right}>
                    <Button
                        type="primary"
                        theme="light"
                        icon="email"
                        onClick={handleContactModalOpen}
                    >
                        E-mail us
                    </Button>
                </div>
            </div>
            <section className={$.section}>
                <div className={$.barheading}>FAQ</div>
                <div className={$.faq}>
                    {faqItems.map((faqItem) => (
                        <div
                            key={faqItem.id}
                            className={cx(
                                $.faq__item,
                                activeQuestion === faqItem.id
                                    ? $["faq__item--active"]
                                    : null,
                            )}
                            onClick={() => setActiveQuestion(faqItem.id)}
                            role="presentation"
                        >
                            <div className={$.faq__item__header}>
                                <div className={$.faq__item__question}>
                                    {faqItem.question}
                                </div>
                                <div className={$.faq__item__icon}>
                                    <Icon
                                        name={
                                            activeQuestion === faqItem.id
                                                ? "arrow-down"
                                                : "plus"
                                        }
                                    />
                                </div>
                            </div>
                            <div className={$.faq__item__answer}>
                                {faqItem.answer}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            {showContactModal && (
                <ContactModal
                    onSend={handleContactModalSend}
                    onClose={handleContactModalClose}
                />
            )}
        </div>
    );
};

export default Support;
